import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import clsx from "clsx";

import PageSizeSelector from "common/components/PaginationFooter/PageSizeSelector";
import { Button } from "common/components/ui/Button";
import { getPaginationInfoText } from "common/helpers/utils";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

interface PaginationFooterProps {
  pageSize: number;
  setPageSize: (pageSize: number) => void;
  selectedPage: number;
  setSelectedPage: (selectedPage: number) => void;
  totalItemCount: number;
  isLastPageHidden: boolean;
}

export default function PaginationFooter({
  pageSize,
  setPageSize,
  selectedPage,
  setSelectedPage,
  totalItemCount,
  isLastPageHidden,
}: PaginationFooterProps) {
  const isDesktop = useTwBreakpoint("lg");

  const pagesCount =
    pageSize > 0
      ? Math.ceil(totalItemCount / pageSize)
      : totalItemCount > 0
        ? 1
        : 0;

  const isEllipsisPagination = pagesCount > 3;
  const pageNumbers = [];
  let isPageNumberOutOfRange = false;

  for (let i = 0; i < pagesCount; i += 1) {
    const isFirstPage = i === 0;
    const isLastPage = i === pagesCount - 1;
    const isCurrentPageWithinOnePageNumber = Math.abs(i - selectedPage) <= 1;

    if (
      isFirstPage ||
      (isLastPage && !isLastPageHidden) ||
      isCurrentPageWithinOnePageNumber
    ) {
      isPageNumberOutOfRange = false;
      const isActive = i === selectedPage;
      pageNumbers.push(
        <Button
          variant={isActive ? "default-black" : "ghost-black"}
          className="select-none !rounded-2xl aspect-square transition-none"
          key={i}
          size={isDesktop ? "md" : "sm"}
          onClick={() => setSelectedPage(i)}
        >
          {i + 1}
        </Button>,
      );
    } else if (!isPageNumberOutOfRange && !isLastPageHidden) {
      isPageNumberOutOfRange = true;
      pageNumbers.push(
        <span key={`ellipsis-${i}`} className="select-none text-black-500">
          ...
        </span>,
      );
    }
  }

  const pageSizeSelectorComponent = (
    <PageSizeSelector pageSize={pageSize} setPageSize={setPageSize} />
  );

  const paginationComponent = pagesCount > 1 && (
    <div className="mx-auto flex items-center justify-center gap-x-1 w-72 lg:w-96">
      <Button
        aria-label="Go to previous page"
        intent="iconOnly"
        className={clsx(
          "!rounded-2xl",
          isEllipsisPagination ? "mr-auto" : "mr-6",
        )}
        variant="outline-black"
        onClick={() => setSelectedPage(selectedPage - 1)}
        disabled={selectedPage === 0}
        size={isDesktop ? "md" : "sm"}
      >
        <CaretLeft />
      </Button>
      {pageNumbers}
      <Button
        aria-label="Go to next page"
        intent="iconOnly"
        className={clsx(
          "!rounded-2xl",
          isEllipsisPagination ? "ml-auto" : "ml-6",
        )}
        variant="outline-black"
        disabled={selectedPage === pagesCount - 1}
        onClick={() => setSelectedPage(selectedPage + 1)}
        size={isDesktop ? "md" : "sm"}
      >
        <CaretRight />
      </Button>
    </div>
  );

  const pageInfoComponent = (
    <span className="text-xs-medium text-black-600">
      {getPaginationInfoText(selectedPage, pageSize, totalItemCount)}
    </span>
  );

  if (isDesktop) {
    return (
      <div className="flex items-center justify-between">
        {pageSizeSelectorComponent}
        {paginationComponent}
        {pageInfoComponent}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-y-4">
      <div className="flex justify-between items-center">
        {pageSizeSelectorComponent}
        {pageInfoComponent}
      </div>
      {paginationComponent}
    </div>
  );
}
